import type { CSSProperties } from 'react'
import { FALLBACK_PARAMS, roundToStep } from '../lib/estimate'
import { duration, huf } from '../lib/format'

/*
  ────────────────────────────────────────────────────────────────────
  A PELDA SZAMAI UGYANABBOL A KEPLETBOL JONNEK, MINT A KALKULATORE.

  A jegy nem kitalalt vegosszeget mutat: a grammot es az idot mi adjuk
  meg (egy kozepes vaza PETG-bol, 0,20 mm-es reteggel), az arat viszont
  a `FALLBACK_PARAMS` szamolja ki - ugyanaz a ket tetel, ugyanaz a
  kerekites, mint az `estimate.ts`-ben. Ha az arlista valtozik, a hero
  vele valtozik; nem allhat itt mas ar, mint amit a latogato ket
  kattintassal ellenoriz.

  A pelda SZANDEKOSAN a rendelesi minimum FOLOTT van. Egy minimum alatti
  pelda azt tanitana, hogy a vegosszeg az anyag + ido - holott akkor a
  minimum a vegosszeg.

  Az ido (11 ó 24 p) azert ilyen „kerek", mert igy a ket tetel osszege
  (2 040 + 1 140) mar eleve 5 Ft tobbszorose: a kerekites nem tesz hozza
  semmit, es a jegyen fejben is pontosan kijon a vegosszeg.
  ────────────────────────────────────────────────────────────────────
*/
const PRICING = FALLBACK_PARAMS.pricing
const EXAMPLE = { file: 'vaza.stl', grams: 240, seconds: 11 * 3600 + 24 * 60 }

const materialHuf = EXAMPLE.grams * PRICING.material_per_gram
const timeHuf = (EXAMPLE.seconds / 3600) * PRICING.energy_per_hour
const totalHuf = roundToStep(materialHuf + timeHuf, PRICING.round_to)
const perGram = PRICING.material_per_gram.toString().replace('.', ',')

/*
  A VAZA PROFILJA: retegenkenti felszelesseg, alulrol felfele.

  Szandekosan kevés, felnagyitott reteg (20): a nyomtatas lenyege - hogy a
  targy vekony, egymasra rakott savokbol all - igy egy pillantasra
  olvashato. Ugyanaz a gondolat, mint a markajel savokra vagott „V"-je.
*/
const PROFILE = [
  72, 81, 88, 94, 98, 100, 100, 98, 94, 89, 82, 75, 68, 61, 56, 54, 54, 56, 61, 65
]
const LAYER_H = 13
const BED_Y = 356
/* A targy a lap bal fele fele tolva: a jobb also negyedet az arjegy takarja. */
const CENTER_X = 196

export default function HeroQuoteVisual(): JSX.Element {
  const topY = BED_Y - PROFILE.length * LAYER_H

  return (
    <div className="print-stage">
      <div className="print-stage__plate">
        <ul className="print-stage__tags" aria-label="A példa beállításai">
          <li>PETG</li>
          <li>0,20 mm réteg</li>
          <li>20% kitöltés</li>
        </ul>

        {/*
          A JELENET DISZITO: a kepernyoolvaso a figure `aria-label`-jet es a
          jegy szoveget kapja, nem a savok szazait.
        */}
        <svg
          className="print-stage__scene"
          viewBox="0 0 520 420"
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            <linearGradient id="vx-layer" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0" stopColor="#d9531f" />
              <stop offset="0.28" stopColor="#ff7a47" />
              <stop offset="0.5" stopColor="#ff9467" />
              <stop offset="0.78" stopColor="#f36a33" />
              <stop offset="1" stopColor="#c84818" />
            </linearGradient>
            <linearGradient id="vx-plate" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0" stopColor="#3a352e" />
              <stop offset="1" stopColor="#211e1a" />
            </linearGradient>
            <radialGradient id="vx-glow" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0" stopColor="#ff6b35" stopOpacity="0.55" />
              <stop offset="1" stopColor="#ff6b35" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* Portal: a nyomtatofej vizszintes sine. */}
          <g className="print-stage__gantry">
            <rect x="36" y="40" width="448" height="6" rx="3" fill="#cbc5ba" />
            <circle cx="40" cy="43" r="7" fill="#b7afa3" />
            <circle cx="480" cy="43" r="7" fill="#b7afa3" />
          </g>

          {/* Meretvonal: magassag. */}
          <g className="print-stage__dim print-stage__dim--y">
            <line x1="58" x2="58" y1={topY} y2={BED_Y} />
            <line x1="50" x2="66" y1={topY} y2={topY} />
            <line x1="50" x2="66" y1={BED_Y} y2={BED_Y} />
            <text x="40" y={(topY + BED_Y) / 2} transform={`rotate(-90 40 ${(topY + BED_Y) / 2})`}>
              118 mm
            </text>
          </g>

          {/* Meretvonal: szelesseg. */}
          <g className="print-stage__dim print-stage__dim--x">
            <line x1={CENTER_X - 100} x2={CENTER_X + 100} y1="398" y2="398" />
            <line x1={CENTER_X - 100} x2={CENTER_X - 100} y1="390" y2="406" />
            <line x1={CENTER_X + 100} x2={CENTER_X + 100} y1="390" y2="406" />
            <text x={CENTER_X} y="392">
              91 mm
            </text>
          </g>

          {/* Nyomtatotalca. */}
          <rect x={CENTER_X - 140} y={BED_Y} width="280" height="14" rx="4" fill="url(#vx-plate)" />
          <rect x={CENTER_X - 140} y={BED_Y} width="280" height="2" rx="1" fill="#5a534a" />

          {/* A targy, rétegenkent. */}
          <g className="print-stage__object">
            {PROFILE.map((half, i) => (
              <rect
                key={i}
                className="print-stage__layer"
                style={{ '--i': i } as CSSProperties}
                x={CENTER_X - half}
                y={BED_Y - (i + 1) * LAYER_H}
                width={half * 2}
                height={LAYER_H + 1}
                rx={LAYER_H / 2}
                fill="url(#vx-layer)"
              />
            ))}
          </g>

          {/* Nyomtatofej a legfelso reteg folott. */}
          <g className="print-stage__head">
            <ellipse cx={CENTER_X} cy={topY - 2} rx="30" ry="10" fill="url(#vx-glow)" />
            <rect x={CENTER_X - 24} y={topY - 62} width="48" height="30" rx="7" fill="#171512" />
            <rect x={CENTER_X - 16} y={topY - 54} width="20" height="3" rx="1.5" fill="#ff6b35" />
            <path
              d={`M${CENTER_X - 9} ${topY - 32} H${CENTER_X + 9} L${CENTER_X + 3} ${topY - 20} H${CENTER_X - 3} Z`}
              fill="#57524a"
            />
          </g>
        </svg>
      </div>

      <div className="print-stage__ticket">
        <div className="ticket__head">
          <span className="ticket__file">{EXAMPLE.file}</span>
          <span className="ticket__badge">Példa</span>
        </div>
        <dl className="ticket__rows">
          <div>
            <dt>Anyag · {EXAMPLE.grams} g × {perGram} Ft</dt>
            <dd>{huf(materialHuf)}</dd>
          </div>
          <div>
            <dt>
              Idő · {duration(EXAMPLE.seconds)} × {PRICING.energy_per_hour} Ft/óra
            </dt>
            <dd>{huf(timeHuf)}</dd>
          </div>
        </dl>
        <div className="ticket__total">
          <span>Gyártási díj</span>
          <strong>{huf(totalHuf)}</strong>
        </div>
        <p className="ticket__foot">
          Rendelési minimum {huf(PRICING.minimum_order)} · előkészítési díj és színfelár nincs
        </p>
      </div>
    </div>
  )
}
